import { useCallback, useMemo } from "react";
import { Collapse, Form, Upload as UploadAntd } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import classname from "classname";
import _ from "lodash";

import { getToken } from "utils/cookies";
import { generateEndpoint } from "utils/helper";
import ItemAttachment from "components/UI/Upload/ItemAttachment";
import useUpload from "components/UI/Upload/hooks/useUpload";
import HideFeature from "components/UI/HideFeature";

const defaultHeader = {
  Authorization: "",
  "X-app": "hr",
};

const Upload = ({
  title,
  name = "attachments",
  formItemName = "attachments",
  payloadName = "attachments[]",
  mimeType = "image/jpeg, image/png, image/jpg, image/webp, image/heic, image/heif, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv, application/zip, application/vnd.rar",
  maxWeight = 10, //max size file upload on MB
  max = 10,
  headers = defaultHeader,
  disabled = false,
  required = false,
  attachments = [],
  action = "", //endpoint attachments
  formItemProps = {},
  onUpload,
  onRemove,
  code,
  expanded = false,
  isAdd,
  customResponseData, //untuk custom response data from server attachments
}) => {
  const { t } = useTranslation();

  const { watchAttachments, beforeUpload, onChange, onPreview, onDownload } =
    useUpload({
      name: formItemName || name,
      max,
      maxWeight,
      mimeType,
      customResponseData,
    });

  const formattedHeaders = useMemo(() => {
    return {
      ...headers,
      Authorization: `Bearer ${getToken()}`,
    };
  }, [headers]);

  const removeHandler = useCallback(
    (file) => {
      if (typeof onRemove === "function") {
        if (file?.id) {
          onRemove(file);
        }
      }
    },
    [onRemove]
  );

  const itemRender = useCallback(
    (originNode, file, fileList, actions) => {
      return (
        <ItemAttachment
          file={file}
          actions={actions}
          disabled={disabled}
          onDownload={onDownload}
          isAdd={isAdd}
        />
      );
    },
    [disabled, isAdd, onDownload]
  );

  const uploadAction = useMemo(() => generateEndpoint(action), [action]);

  const UploadComponent = disabled ? UploadAntd : UploadAntd.Dragger;

  const items = useMemo(
    () => [
      {
        key: "1",
        label: (
          <div className="flex flex-row gap-x-1">
            {!!required && <span className="text-red1">*</span>}
            <span>{title || t("employee.attachment")}</span>
          </div>
        ),
        children: (
          <HideFeature code={code} disabled={!code}>
            <Form.Item
              name={formItemName || name}
              className="mb-0"
              {...formItemProps}
            >
              <UploadComponent
                name={payloadName}
                accept={mimeType}
                headers={formattedHeaders}
                disabled={disabled}
                fileList={watchAttachments || []}
                action={uploadAction}
                itemRender={itemRender}
                beforeUpload={beforeUpload}
                onChange={onChange}
                onPreview={onPreview}
                onRemove={removeHandler}
                listType="text"
                maxCount={max}
                multiple
              >
                {!disabled && (
                  <>
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      {t("property.click_or_drag_file_to_this_area_to_upload")}
                    </p>
                    <div className="ant-upload-hint text-gray-500">
                      <ul>
                        <li>
                          {t("attachment.maximum_file_size", {
                            value: maxWeight,
                          })}
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </UploadComponent>
            </Form.Item>
          </HideFeature>
        ),
      },
    ],
    [
      beforeUpload,
      disabled,
      formItemProps,
      formattedHeaders,
      itemRender,
      maxWeight,
      mimeType,
      name,
      formItemName,
      onChange,
      onPreview,
      payloadName,
      removeHandler,
      t,
      uploadAction,
      watchAttachments,
      required,
      code,
      max,
      title,
    ]
  );

  return (
    <>
      <Collapse
        className={classname({
          "mb-5":
            !required ||
            (!!required && !_.isEmpty(watchAttachments)) ||
            disabled,
          hidden: !!disabled && _.isEmpty(attachments),
        })}
        defaultActiveKey={(required && !disabled) || expanded ? ["1"] : []}
        items={items}
      />
      {!!required && !disabled && _.isEmpty(watchAttachments) && (
        <div className="my-1">
          <span className="text-red1">
            {t("settingOvertime.please_input", {
              value: t("employee.attachment").toLowerCase(),
            })}
          </span>
        </div>
      )}
    </>
  );
};

export default Upload;
